import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { VList } from 'virtua';
// mantine
import { Divider, TextInput, Switch, Loader } from '@mantine/core';
import { useDebouncedState } from '@mantine/hooks';
// icons
import { Search, Settings } from 'lucide-react';
// components
import { PopoverButton } from '@newo/ui-kit';
import { IConversationsUserPersona } from '@newo/api/gateway';
import PersonaItem from '@/pages/Conversations/components/Personas/components/PersonaItem';
import NoRecords from '@/components/NoRecords';
// utils
import useConversationStore from '@/pages/Conversations/Conversations.store.ts';
import {
    usePaginatedConversationsUserPersonasQuery,
    useConversationsUserPersonaQuery
} from '@/services/bff/bff.queries.ts';

interface PersonasProps {
    className?: string;
}

const DEBOUNCE_TIMEOUT = 200;

const Personas = ({ className }: PersonasProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [query, setQuery] = useDebouncedState('', DEBOUNCE_TIMEOUT);
    const queryPersonaId = searchParams.get('user_persona_id') ?? undefined;

    const {
        userPersonaFilters,
        setUserPersonaFilters,
        selectedUserPersona,
        selectUserPersona
    } = useConversationStore(
        useShallow((state) => ({
            userPersonaFilters: state.userPersonaFilters,
            setUserPersonaFilters: state.setUserPersonaFilters,
            selectedUserPersona: state.selectedUserPersona,
            selectUserPersona: state.selectUserPersona
        }))
    );

    const {
        data,
        isSuccess,
        isFetching,
        isLoading,
        hasNextPage,
        fetchNextPage
    } = usePaginatedConversationsUserPersonasQuery({
        ...userPersonaFilters,
        query: query.trim() || undefined,
        page: 1,
        per: 50
    });
    const firstPersona = data?.pages[0]?.items[0];

    const { data: userPersona } = useConversationsUserPersonaQuery(
        selectedUserPersona ? undefined : queryPersonaId
    );

    // if user_persona_id is in query params and user persona is not selected, fetch user persona and select it
    useEffect(() => {
        if (!selectedUserPersona && userPersona && queryPersonaId) {
            selectUserPersona(userPersona);
        }
    }, [queryPersonaId, selectUserPersona, selectedUserPersona, userPersona]);

    // if user persona is not selected and user_persona_id is not in query params, set first persona as selected
    useEffect(() => {
        if (!selectedUserPersona && firstPersona && !queryPersonaId) {
            selectUserPersona(firstPersona);
        }
    }, [firstPersona, queryPersonaId, selectUserPersona, selectedUserPersona]);

    const personas = useMemo(() => {
        const items = data?.pages.flatMap((page) => page.items) ?? [];

        if (selectedUserPersona) {
            const isSelectedInItems = items.some(
                ({ id }) => selectedUserPersona.id === id
            );
            return isSelectedInItems ? items : [selectedUserPersona, ...items];
        }
        return items;
    }, [data?.pages, selectedUserPersona]);

    const handleRangeChange = (_: number, end: number) => {
        const SHIFT = 20;
        if (end + SHIFT > personas.length && hasNextPage && !isFetching) {
            fetchNextPage();
        }
    };

    const handleSelectUserPersona = (persona?: IConversationsUserPersona) => {
        if (persona) {
            selectUserPersona(persona);
            searchParams.set('user_persona_id', persona.id);
        } else {
            searchParams.delete('user_persona_id');
        }
        setSearchParams(searchParams);
    };

    return (
        <div className={clsx('overflow-hidden flex flex-col', className)}>
            <div className="py-[14px] px-4 shadow-xs">
                <div className="flex justify-between">
                    <div className="text-xs font-semibold">User personas</div>
                    <PopoverButton
                        title="Add Event Subscription"
                        trigger={<Settings />}
                    >
                        {() => (
                            <div className="flex flex-col gap-4">
                                <Divider label="Filters" labelPosition="left" />
                                <Switch
                                    checked={
                                        userPersonaFilters.skip_without_user_messages
                                    }
                                    label="Hide empty"
                                    size="xs"
                                    onChange={(event) => {
                                        setUserPersonaFilters({
                                            skip_without_user_messages:
                                                event.currentTarget.checked
                                        });
                                    }}
                                />
                                <Switch
                                    checked={userPersonaFilters.hide_not_found}
                                    label="Hide unfound"
                                    size="xs"
                                    onChange={(event) => {
                                        setUserPersonaFilters({
                                            hide_not_found:
                                                event.currentTarget.checked
                                        });
                                    }}
                                />
                            </div>
                        )}
                    </PopoverButton>
                </div>

                <TextInput
                    defaultValue={query}
                    placeholder="Search"
                    rightSection={<Search />}
                    onChange={(event) => {
                        setQuery(event.currentTarget.value);
                    }}
                />
            </div>
            <VList className="flex-1" onRangeChange={handleRangeChange}>
                {isSuccess && personas.length === 0 ? (
                    <NoRecords>No personas found</NoRecords>
                ) : undefined}
                {personas.map((persona) => (
                    <PersonaItem
                        isSelected={selectedUserPersona?.id === persona.id}
                        key={persona.id}
                        persona={persona}
                        selectUserPersona={handleSelectUserPersona}
                        onFilterChange={setUserPersonaFilters}
                    />
                ))}
                {isLoading ? (
                    <Loader className="mx-auto my-40 block" size="sm" />
                ) : undefined}
            </VList>
        </div>
    );
};

export default Personas;
